import {
  IMyFilesMediaItem,
  IBaseMediaItem,
  TMediaItemType,
  MediaStatus,
  IUploadMediaItem
} from 'types/media'

import { FIT } from 'consts/canvas'
import { IMAGE_EXTENSIONS } from 'consts/uploadTypes'
import { clampFontSize, clampStrokeThickness } from 'utils/text'

import { IDimensions, TScaleMode } from 'types'
import { IMediaItem } from 'types/media'
import { DEFAULT_MEDIA_DURATION, PROJECT_MEDIA_TYPES } from 'consts'
import { ITtsItemData } from 'types/text-to-speech'
import { IAudioItem } from 'types/audio'
import { IUserMediaUploadResponse } from 'types/userMediaUpload'
import { IMediaLibConfig } from 'components/UploadArea/types'

interface ICalculateMediaRectParams {
  mediaDimensions: IDimensions
  mode: TScaleMode
  canvasDimensions: IDimensions
  rotation: number
}

export const calculateMediaRect = ({
  mediaDimensions,
  mode,
  canvasDimensions,
  rotation
}: ICalculateMediaRectParams) => {
  const isRotated = rotation % 180 !== 0

  const mediaWidth = isRotated ? mediaDimensions.height : mediaDimensions.width
  const mediaHeight = isRotated ? mediaDimensions.width : mediaDimensions.height

  const { width: canvasWidth, height: canvasHeight } = canvasDimensions

  const mediaAspectRatio = mediaWidth / mediaHeight
  const canvasAspectRatio = canvasWidth / canvasHeight

  const shouldScaleToWidth =
    mode === FIT
      ? mediaAspectRatio > canvasAspectRatio
      : mediaAspectRatio < canvasAspectRatio

  const scaledDimensions = shouldScaleToWidth
    ? {
        width: canvasWidth,
        height: canvasWidth / mediaAspectRatio
      }
    : {
        width: canvasHeight * mediaAspectRatio,
        height: canvasHeight
      }

  const finalDimensions = isRotated
    ? {
        width: scaledDimensions.height,
        height: scaledDimensions.width
      }
    : scaledDimensions

  const x = (canvasWidth - finalDimensions.width) / 2
  const y = (canvasHeight - finalDimensions.height) / 2

  return {
    ...finalDimensions,
    x,
    y
  }
}

interface IPreserveScaleMediaRectParams {
  currentWidth: number
  currentHeight: number
  currentCoordinates: { x: number; y: number }
  oldOriginal: IDimensions
  newOriginal: IDimensions
  oldCanvas: IDimensions
  newCanvas: IDimensions
  rotation: number
}

// Preserves the user's manual resize/reposition across regeneration or canvas
// aspect-ratio switches. Computes the scale/offset the user applied relative to
// the OLD FIT baseline and re-applies it on top of the NEW FIT baseline. If the
// user never resized or moved the item, scale is 1 and offset is 0, so the
// output matches a plain FIT recompute.
export const preserveScaleMediaRect = ({
  currentWidth,
  currentHeight,
  currentCoordinates,
  oldOriginal,
  newOriginal,
  oldCanvas,
  newCanvas,
  rotation
}: IPreserveScaleMediaRectParams) => {
  const oldFit = calculateMediaRect({
    mediaDimensions: oldOriginal,
    mode: FIT,
    canvasDimensions: oldCanvas,
    rotation
  })
  const newFit = calculateMediaRect({
    mediaDimensions: newOriginal,
    mode: FIT,
    canvasDimensions: newCanvas,
    rotation
  })

  const scaleX = oldFit.width ? currentWidth / oldFit.width : 1
  const scaleY = oldFit.height ? currentHeight / oldFit.height : 1

  return {
    width: newFit.width * scaleX,
    height: newFit.height * scaleY,
    x: newFit.x + (currentCoordinates.x - oldFit.x),
    y: newFit.y + (currentCoordinates.y - oldFit.y)
  }
}

interface IResolveCompletedMediaRectParams {
  currentWidth: number
  currentHeight: number
  currentCoordinates: { x: number; y: number }
  newOriginal: IDimensions
  canvasDimensions: IDimensions
  rotation: number
}

const ASPECT_RATIO_EPSILON = 0.01

// Decides which rect to apply to a timeline item when an AI (re)generation
// completes. The goal is to ensure the bounding box aspect always matches the
// new asset's aspect (the WebWood engine stretches images to fit the bounding
// box via setDisplaySize, so any mismatch shows as a distorted image).
//
// - If the current bounding box already matches the new asset's aspect, the
//   item's current rect is kept verbatim so any user resize/reposition
//   (before or during regeneration) survives completion.
// - Otherwise (the user picked a different aspect for the regeneration, or
//   the bounding box drifted from the previous asset's aspect for any reason),
//   a fresh FIT-to-canvas rect is returned so the new asset isn't stretched
//   inside the old bounding box.
export const resolveCompletedMediaRect = ({
  currentWidth,
  currentHeight,
  currentCoordinates,
  newOriginal,
  canvasDimensions,
  rotation
}: IResolveCompletedMediaRectParams) => {
  const safeCurrentHeight = currentHeight || 1
  const safeNewHeight = newOriginal.height || 1
  const currentAspect = currentWidth / safeCurrentHeight
  const newAspect = newOriginal.width / safeNewHeight
  const aspectMatches =
    Math.abs(currentAspect - newAspect) <= ASPECT_RATIO_EPSILON

  if (aspectMatches && currentWidth > 0 && currentHeight > 0) {
    return {
      width: currentWidth,
      height: currentHeight,
      x: currentCoordinates.x,
      y: currentCoordinates.y
    }
  }

  return calculateMediaRect({
    mediaDimensions: newOriginal,
    mode: FIT,
    canvasDimensions,
    rotation
  })
}

// Recalculates a text item's rect, position and font values when switching between
// landscape (1920×1080) and portrait (1080×1920).
//
// scaleX = newCanvasWidth / oldCanvasWidth = canvasWidth / canvasHeight
//   → 0.5625 for L→P (scale down), 1.778 for P→L (scale up)
//
// Y position: L→P adds the letterbox offset (video gains black bars top/bottom);
//             P→L subtracts it before scaling (inverse transform).
//
// fontSize and strokeThickness are clamped to their valid editor ranges
// (MIN/MAX_FONT_SIZE, MIN/MAX_STROKE_THICKNESS). When either value gets
// clamped, width/height are kept at the item's current values so the bounding
// box matches the clamped glyphs (no phantom whitespace); x/y still scale so
// the text lands at a sensible position on the resized canvas.
export const calcTextRectForAspectRatioSwitch = (
  item: IMediaItem,
  oldCanvas: IDimensions,
  newCanvas: IDimensions
) => {
  const oldCanvasSmallestSize = Math.min(oldCanvas.width, oldCanvas.height)
  const newCanvasSmallestSize = Math.min(newCanvas.width, newCanvas.height)
  const scale = newCanvasSmallestSize / oldCanvasSmallestSize

  const fontSize = item.meta.textData?.fontSize ?? 0
  const strokeThickness = item.meta.textData?.strokeThickness ?? 0

  const rawFontSize = Math.round(fontSize * scale)
  const rawStrokeThickness = Math.round(strokeThickness * scale)
  const clampedFontSize = clampFontSize(rawFontSize)
  const clampedStrokeThickness = clampStrokeThickness(rawStrokeThickness)

  const isClamped =
    clampedFontSize !== rawFontSize ||
    clampedStrokeThickness !== rawStrokeThickness

  const relativeX = (item.coordinates.x + item.width / 2) / oldCanvas.width
  const relativeY = (item.coordinates.y + item.height / 2) / oldCanvas.height

  const x = newCanvas.width * relativeX - item.width / 2
  const y = newCanvas.height * relativeY - item.height / 2

  if (isClamped) {
    return {
      width: item.width,
      height: item.height,
      x,
      y,
      fontSize: clampedFontSize,
      strokeThickness: clampedStrokeThickness
    }
  }

  return {
    width: item.width * scale,
    height: item.height * scale,
    x,
    y,
    fontSize: clampedFontSize,
    strokeThickness: clampedStrokeThickness
  }
}

// A media asset is an avatar (vs a plain AI video) iff it carries an
// `aiAvatarProjectId` in its meta. Avatars share the ai-video table/API *and* the
// host My Files store, so this presence check is the single discriminator used
// both for in-app ai-video results (`isAvatarVideoProject`) and host
// media-library items (`getMediaItemType`). Keep this the one source of truth.
export const hasAiAvatarMarker = (
  meta?: { aiAvatarProjectId?: number } | null
): boolean => typeof meta?.aiAvatarProjectId === 'number'

const getMediaItemType = (
  sourceType: IMyFilesMediaItem['sourceType'],
  mime: string,
  meta?: IMyFilesMediaItem['meta']
): TMediaItemType => {
  // Avatars come back from My Files tagged `ai-video`; the avatar marker takes
  // precedence so a selected avatar opens the AI Avatar tab on click.
  if (hasAiAvatarMarker(meta)) {
    return PROJECT_MEDIA_TYPES.aiAvatar
  }

  const sourceTypeMap: Record<string, TMediaItemType> = {
    'ai-image': PROJECT_MEDIA_TYPES.aiImage,
    'ai-video': PROJECT_MEDIA_TYPES.aiVideo
  }
  const mimeType = mime.split('/')[0]

  if (sourceType && sourceType in sourceTypeMap) {
    return sourceTypeMap[sourceType]
  }

  return mimeType === 'image'
    ? PROJECT_MEDIA_TYPES.image
    : PROJECT_MEDIA_TYPES.video
}

const getVideoDurationMeta = (
  file: IMyFilesMediaItem,
  isImage: boolean
): Partial<IMediaItem['meta']> => {
  if (isImage || !file?.duration) {
    return {}
  }

  return {
    originalDuration: file.duration,
    trimStart: 0,
    previewPath: file.thumbnailVideo,
    volume: 100
  }
}

interface IClampDurationToSourceParams {
  duration: number
  trimStart?: number
  originalDuration?: number
  playbackSpeed?: number
}

/**
 * A clip must never consume more source seconds than the source has:
 * duration × playbackSpeed ≤ originalDuration − trimStart. Items without a
 * known source length (images, text) are returned unchanged. Returns 0 when
 * trimStart ≥ originalDuration — callers decide the degenerate fallback.
 * Works for audio items too (trimStart = mediaStart, originalDuration =
 * mediaDuration).
 */
export const clampDurationToSource = ({
  duration,
  trimStart = 0,
  originalDuration,
  playbackSpeed = 1
}: IClampDurationToSourceParams): number => {
  if (
    originalDuration === undefined ||
    originalDuration <= 0 ||
    playbackSpeed <= 0
  ) {
    return duration
  }
  const maxDuration =
    Math.max(0, originalDuration - Math.max(0, trimStart)) / playbackSpeed
  return Math.min(duration, maxDuration)
}

const createMediaItem = (
  file: IMyFilesMediaItem,
  mediaRect: ReturnType<typeof calculateMediaRect>,
  startTime: number,
  mimeType: string,
  zIndex: number = 0
): IMediaItem => {
  const isImage = mimeType.startsWith('image/')
  const itemType = getMediaItemType(file.sourceType, mimeType, file.meta)
  const duration = file.duration || DEFAULT_MEDIA_DURATION
  const assetId = file?.assetId ? file.assetId.toString() : file.id.toString()

  return {
    id: crypto.randomUUID(),
    assetId,
    type: itemType,
    height: mediaRect.height,
    width: mediaRect.width,
    rotation: 0,
    duration,
    startTime,
    zIndex,
    coordinates: {
      x: mediaRect.x,
      y: mediaRect.y
    },
    meta: {
      path: file.filePath,
      originalHeight: file.height,
      originalWidth: file.width,
      status: MediaStatus.OK,
      ...getVideoDurationMeta(file, isImage)
    }
  }
}

export const calculateInsertionStartTime = (
  playheadTime: number,
  existingItems: IMediaItem[],
  newItemsTotalDuration: number
): number => {
  if (!existingItems.length) return playheadTime

  const items = sortMediaItemsByStartTime(existingItems)

  const spaceToNextItem = items.reduce((spaceToNextItem, item) => {
    if (spaceToNextItem !== Infinity) return spaceToNextItem

    const itemStart = item.startTime
    const itemEnd = item.startTime + item.duration

    // playhead inside item → insert at end
    if (playheadTime >= itemStart && playheadTime < itemEnd) {
      return 0
    }

    // first item after playhead → defines available space
    if (itemStart >= playheadTime) {
      return itemStart - playheadTime
    }

    return spaceToNextItem
  }, Infinity)

  // not enough space → insert at end
  if (spaceToNextItem === 0 || spaceToNextItem < newItemsTotalDuration) {
    return getLastMediaItemEndTime(items)
  }

  return playheadTime
}

export const convertMediaLibraryItemsToMediaItems = (
  files: IMyFilesMediaItem[],
  insertionStartTime: number = 0,
  canvasDimensions: IDimensions,
  zIndex: number = 0
): IMediaItem[] => {
  const items: IMediaItem[] = []
  let currentStartTime = insertionStartTime

  for (const file of files) {
    const mediaRect = calculateMediaRect({
      mediaDimensions: { width: file.width, height: file.height },
      mode: FIT,
      canvasDimensions,
      rotation: 0
    })

    const item = createMediaItem(
      file,
      mediaRect,
      currentStartTime,
      file.mime,
      zIndex
    )

    items.push(item)
    currentStartTime += item.duration
  }

  return items
}

export const captureVideoFrame = async (
  videoUrl: string,
  timestamp: number = 0
): Promise<string> => {
  const video = document.createElement('video')
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  video.crossOrigin = 'anonymous'
  video.src = videoUrl

  await new Promise((resolve, reject) => {
    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      video.currentTime = timestamp
    }
    video.onseeked = resolve
    video.onerror = reject
  })

  context?.drawImage(video, 0, 0, canvas.width, canvas.height)
  const frame = canvas.toDataURL('image/jpeg')

  video.remove()
  canvas.remove()

  return frame
}

export const convertBaseItemToMediaItem = (
  mediaItem: IBaseMediaItem,
  insertionStartTime: number = 0,
  canvasDimensions: IDimensions,
  zIndex: number = 0
) => {
  const startTime = insertionStartTime
  const width = mediaItem.originalWidth || 0
  const height = mediaItem.originalHeight || 0

  const mediaRect = calculateMediaRect({
    mediaDimensions: { width, height },
    mode: FIT,
    canvasDimensions,
    rotation: 0
  })

  const getDurationMeta = () => {
    if (
      mediaItem.type !== PROJECT_MEDIA_TYPES.image &&
      mediaItem.type !== PROJECT_MEDIA_TYPES.aiImage &&
      mediaItem.duration
    ) {
      return {
        originalDuration: mediaItem.duration,
        trimStart: 0,
        volume: 100
      }
    }
    return {}
  }

  return {
    id: mediaItem.id || crypto.randomUUID(),
    assetId: mediaItem.assetId || crypto.randomUUID(),
    type: mediaItem.type,
    height: mediaRect.height,
    width: mediaRect.width,
    rotation: 0,
    duration: mediaItem.duration,
    startTime,
    zIndex,
    coordinates: {
      x: mediaRect.x,
      y: mediaRect.y
    },
    meta: {
      path: mediaItem.path,
      previewPath: mediaItem.previewPath,
      originalHeight: mediaRect.height,
      originalWidth: mediaRect.width,
      status: mediaItem.status || MediaStatus.OK,
      assetParentId: mediaItem.assetParentId,
      ...getDurationMeta()
    }
  }
}

export const mergeRecentlyUsedItems = (
  existing: IBaseMediaItem[],
  newItems: IMediaItem[]
) => {
  const storageItems: IBaseMediaItem[] = newItems.map(item => ({
    fileName: item.meta.path?.split('/').pop() || '',
    assetId: item.assetId,
    type: item.type,
    originalWidth: item.meta.originalWidth || 0,
    originalHeight: item.meta.originalHeight || 0,
    path: item.meta?.path,
    duration:
      item.type === PROJECT_MEDIA_TYPES.image
        ? DEFAULT_MEDIA_DURATION
        : item.duration
  }))

  const uniques = storageItems.filter(
    item => !existing.some(i => i.path === item.path)
  )
  const merged = [...uniques, ...existing]

  return merged.splice(0, 30)
}

export const calculateTotalMediaDuration = (
  mediaItems: IMediaItem[]
): number => {
  return mediaItems.reduce(
    (acc: number, item: IMediaItem) =>
      acc + (item.duration || DEFAULT_MEDIA_DURATION),
    0
  )
}

export const convertTtsItemIntoAudioItem = (
  ttsItem: ITtsItemData
): IAudioItem => ({
  id: crypto.randomUUID(),
  assetId: `${ttsItem.id}`,
  duration: ttsItem.duration,
  mediaStart: 0,
  mediaDuration: ttsItem.duration,
  projectStart: 0,
  mediaPath: ttsItem.filePath,
  title: ttsItem.text,
  type: 'TTS',
  volume: 100,
  lineIndex: 0
})

export const isVisualMediaItem = (type: string | null): boolean => {
  const visualMediaTypes = Object.values(PROJECT_MEDIA_TYPES) as string[]
  return type ? visualMediaTypes.includes(type) : false
}

export const isAiVideoPending = (item: IMediaItem): boolean => {
  return (
    item.type === PROJECT_MEDIA_TYPES.aiVideo &&
    item.meta.status === MediaStatus.PENDING
  )
}

export const mapMediaInputAndOutput = (
  inputMediaFiles: Array<IUploadMediaItem>,
  data: IUserMediaUploadResponse
) => {
  const images = inputMediaFiles.filter(
    media => media.type === PROJECT_MEDIA_TYPES.image
  )
  const videos = inputMediaFiles.filter(
    media => media.type === PROJECT_MEDIA_TYPES.video
  )

  return [
    ...images.map((image, index) => ({
      id: String(image.id),
      filePath: data.images![index].filePath,
      width: data.images![index].width,
      height: data.images![index].height
    })),
    ...videos.map((video, index) => ({
      id: String(video.id),
      assetId: String(data.videos![index].id),
      filePath: data.videos![index].filePath,
      width: data.videos![index].width,
      height: data.videos![index].height,
      duration:
        data.videos![index].durationFloat ?? data.videos![index].duration,
      smallFilePath: data.videos![index].thumbnailVideo
    }))
  ]
}

export const isImagePath = (path?: string | null): boolean => {
  if (!path) return false
  const extension = path.split('.').pop()
  if (!extension) return false
  return IMAGE_EXTENSIONS.includes(
    extension as (typeof IMAGE_EXTENSIONS)[number]
  )
}

export const calculateTotalFilesSize = (
  files: Array<File | IMyFilesMediaItem>
): number =>
  files.reduce(
    (total, file) =>
      total + (file instanceof File ? file.size : file.fileSize || 0),
    0
  )

/**
 * Source trim start in seconds (where playback begins in the file).
 * Matches v2ww `convertVideoMetadata`: `meta.trimStart` (seconds), else timeline `startTime`.
 */
export const getMediaSourceTrimStartSeconds = (item: IMediaItem): number =>
  item.meta.trimStart ?? item.startTime

export const getLastMediaItemEndTime = (mediaItems: IMediaItem[]) => {
  return mediaItems.reduce(
    (max, item) => Math.max(max, item.startTime + item.duration),
    0
  )
}

export const sortMediaItemsByStartTime = (mediaItems: IMediaItem[]) =>
  [...mediaItems].sort((a, b) => a.startTime - b.startTime)

export const openMediaLib = (config: IMediaLibConfig) => {
  const defaultConfig = {
    fileType: 'image',
    onClose: window.closeMediaLib,
    mediaLibUnmountCb: window.closeMediaLib,
    maxFileImportCount: 1,
    isVisible: true,
    onChoose: () => {}
  }
  window.openMediaLib({ ...defaultConfig, ...config })
}
